import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, BusinessCardData } from "../../../../services/api";
import { Note } from "../../styles";
import { WalletButton, WalletButtons, WalletsPart } from "./styles";

const prefix = "pages.businessCard.wallets";

type Wallet = "apple" | "google";

/**
 * Where a tab only opens right on the tap, not once a request is back: Safari (on the Mac too) and every browser of the
 * iPhone and the iPad (Chrome, Firefox, the ones inside Instagram or WhatsApp... all of them are WebKit).
 */
const opensTabOnTap = () => {
  const { userAgent, platform, maxTouchPoints } = navigator;
  const safari = /^((?!chrome|chromium|crios|fxios|edg|opr|android).)*safari/i.test(userAgent);
  // an iPad asks for the desktop site and says it is a Mac, but it has a touch screen
  const ios = /iPad|iPhone|iPod/.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1);
  return safari || ios;
};

/** The status of the answer of the API, when there is one. */
const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

type Props = {
  siteId: string;
  data: BusinessCardData;
};

// The wallet each button adds the card to, drawn as a stack of cards in its colors
const WalletIcon = ({ wallet }: { wallet: Wallet }) => (
  <svg width="28" height="22" viewBox="0 0 28 22" aria-hidden="true">
    {(wallet === "apple" ? ["#34c759", "#ffcc00", "#ff3b30"] : ["#4285f4", "#34a853", "#fbbc05"]).map(
      (color, index) => (
        <rect key={color} x="0" y={index * 4} width="28" height="12" rx="3" fill={color} />
      )
    )}
    <rect x="0" y="10" width="28" height="12" rx="3" fill={wallet === "apple" ? "#e5e5ea" : "#ea4335"} />
  </svg>
);

/** The card in the phone's wallets (Apple, Google), for any published Keepo (FREE or PLUS). */
export const Wallets = ({ siteId, data }: Props) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<Wallet>();
  const [message, setMessage] = useState<string>();

  const add = async (wallet: Wallet) => {
    // The browsers that block a tab opened later: it opens now, on the tap, blank; it goes to Google once the link is made
    const tab = wallet === "google" && opensTabOnTap() ? window.open("", "_blank") : null;
    if (tab) tab.opener = null;
    setBusy(wallet);
    setMessage(undefined);
    try {
      if (wallet === "apple") {
        // the pass downloads; on an iPhone it opens straight in the Wallet
        const pass = await api.getAppleWalletPass(siteId);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(pass);
        link.download = "cartao-keepo.pkpass";
        link.click();
        URL.revokeObjectURL(link.href);
      } else {
        const url = await api.getGoogleWalletUrl(siteId);
        if (tab) tab.location.href = url;
        else window.open(url, "_blank", "noopener");
      }
    } catch (error) {
      tab?.close();
      // the account of Keepo with that wallet isn't set up (503): not something to try again right now
      setMessage(t(`${prefix}.${statusOf(error) === 503 ? "unavailable" : "error"}`));
    } finally {
      setBusy(undefined);
    }
  };

  return (
    <WalletsPart aria-label={t(`${prefix}.title`)}>
      <WalletButtons>
        {(["apple", "google"] as Wallet[]).map(wallet => {
          // a wallet whose account with Keepo isn't set up yet (Apple, for now) shows dimmed until it is: nothing
          // written on it, only its name for a screen reader and for the mouse over it "<wallet> - coming soon"
          const soon = !data.wallets[wallet];
          const soonLabel = t(`${prefix}.soon`, { wallet: t(`${prefix}.${wallet}`) });
          return (
            <WalletButton
              key={wallet}
              type="button"
              // not `disabled`: a disabled button shows no tip (title) in some browsers
              aria-disabled={soon || busy !== undefined}
              aria-label={soon ? soonLabel : undefined}
              title={soon ? soonLabel : undefined}
              aria-busy={busy === wallet}
              onClick={() => !soon && busy === undefined && add(wallet)}>
              <WalletIcon wallet={wallet} />
              <span>
                <small>{t(`${prefix}.add`)}</small>
                {t(`${prefix}.${wallet}`)}
              </span>
            </WalletButton>
          );
        })}
      </WalletButtons>

      {message && <Note role="alert">{message}</Note>}
    </WalletsPart>
  );
};
